'use client';

import { PatternCard } from '../patterns/PatternCard';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import type { Pattern } from '@/lib/supabase';
import { Search } from 'lucide-react';
import { motion } from 'framer-motion';
import Link from 'next/link';

interface SearchResultsProps {
  results?: Pattern[];
  query?: string;
  isLoading?: boolean;
  totalResults?: number;
  onViewAll?: () => void;
  onClearFilters?: () => void;  // NEW: Callback to clear filters
}

const SkeletonCard = () => (
  <div className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4" />
    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
    <div className="h-4 bg-gray-200 rounded w-5/6 mb-4" />
    <div className="h-20 bg-gray-200 rounded w-full mb-4" />
    <div className="flex gap-2 mb-4">
      <div className="h-6 bg-gray-200 rounded w-16" />
      <div className="h-6 bg-gray-200 rounded w-16" />
    </div>
    <div className="h-10 bg-gray-200 rounded w-full" />
  </div>
);

const EmptyState = ({ query, onClearFilters }: { query?: string; onClearFilters?: () => void }) => (
  <div className="text-center py-16" role="status" aria-live="polite">
    <div className="flex justify-center mb-4">
      <div className="bg-gray-100 p-4 rounded-full">
        <Search className="w-12 h-12 text-gray-400" aria-hidden="true" />
      </div>
    </div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">
      {query ? `No patterns found for "${query}"` : 'No results yet'}
    </h3>
    <p className="text-gray-600 mb-6 max-w-md mx-auto">
      {query
        ? 'Try different keywords or browse all patterns to discover what you need.'
        : 'Start typing to search for patterns, frameworks, or tags.'}
    </p>
    <div className="flex gap-3 justify-center">
      {onClearFilters && (
        <Button variant="secondary" size="sm" onClick={onClearFilters}>
          Clear Filters
        </Button>
      )}
      <Link href="/browse">
        <Button variant="default" size="sm">
          Browse All Patterns
        </Button>
      </Link>
    </div>
  </div>
);

const ResultsHeader = ({ count, query }: { count: number; query?: string }) => (
  <div className="flex items-center gap-3 mb-6">
    <h2 className="text-2xl font-bold text-gray-900">
      {query ? `Search Results` : 'Results'}
    </h2>
    <Badge variant="default" size="sm" aria-label={`${count.toLocaleString()} ${count === 1 ? 'pattern' : 'patterns'} found`}>
      {count.toLocaleString()} {count === 1 ? 'pattern' : 'patterns'}
    </Badge>
    {query && (
      <span className="text-gray-600">
        for &quot;{query}&quot;
      </span>
    )}
  </div>
);

export function SearchResults({
  results,
  query,
  isLoading,
  totalResults,
  onViewAll,
  onClearFilters,
}: SearchResultsProps) {
  const displayLimit = 6;
  const displayResults = results?.slice(0, displayLimit);
  const hasMore = (totalResults ?? results?.length ?? 0) > displayLimit;
  const remainingCount = (totalResults ?? results?.length ?? 0) - displayLimit;

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="h-8 bg-gray-200 rounded w-48 mb-6 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!displayResults || displayResults.length === 0) {
    return <EmptyState query={query} onClearFilters={onClearFilters} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full"
      role="region"
      aria-live="polite"
      aria-atomic="true"
    >
      <ResultsHeader count={totalResults ?? results?.length ?? 0} query={query} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" role="list" aria-label="Search results">
        {displayResults.map((pattern, index) => (
          <motion.div
            key={pattern.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            role="listitem"
          >
            <PatternCard pattern={pattern} />
          </motion.div>
        ))}
      </div>

      {hasMore && onViewAll && (
        <div className="mt-8 text-center">
          <Button
            variant="secondary"
            size="lg"
            onClick={onViewAll}
            className="min-w-[200px]"
          >
            View all {remainingCount.toLocaleString()} remaining {remainingCount === 1 ? 'result' : 'results'}
          </Button>
        </div>
      )}
    </motion.div>
  );
}
